import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import _, { groupBy } from 'lodash';
import type { TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type { IDefaultReducerState } from 'types/state.interface';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import {
  EAppId,
  type IMapping,
  type IObjectRelationship,
  type IObjectRelationships,
} from '@timeedit/types';
import api from '../services/api.service';

type TObjectRelationshipState = IDefaultReducerState &
  IObjectRelationships & {
    objectMappings: IMapping;
    mappedRelationship: Record<string, string[]>;
  };

export const initialState: TObjectRelationshipState = {
  loading: false,
  hasErrors: false,
  appId: EAppId.TE_EXAM,
  relationships: [],
  mappedRelationship: {},
  objectMappings: {
    appId: EAppId.TE_EXAM,
    objectTypes: [],
  },
};

// Slice
const slice = createSlice({
  name: 'objectRelationships',
  initialState,
  reducers: {
    fetchObjectRelationshipsRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchObjectRelationshipsSuccess: (
      state,
      { payload }: PayloadAction<{ relationships: IObjectRelationship[] }>
    ) => {
      state.relationships = payload.relationships;
      const groupedRelationships = groupBy(payload.relationships, 'from');
      state.mappedRelationship = Object.keys(groupedRelationships).reduce(
        (results, field) => {
          return {
            ...results,
            [field]: groupedRelationships[field].map(({ to }) => to),
          };
        },
        {}
      );
      finishedLoadingSuccess(state);
    },
    fetchObjectMappingSuccess: (
      state,
      { payload }: PayloadAction<IMapping>
    ) => {
      state.objectMappings = payload;
      finishedLoadingSuccess(state);
    },
    fetchObjectRelationshipsFailure: (state) => {
      finishedLoadingFailure(state);
    },
  },
});

export default slice.reducer;

// Selectors
export const objectRelationshipsSelector = (state: TRootState) =>
  state.objectRelationships.relationships;
export const mappingObjectTypesSelector = (state: TRootState) =>
  state.objectRelationships.objectMappings.objectTypes;
export const objectRelationshipsLoading = (state: TRootState) =>
  state.objectRelationships.loading;

// Actions
export const {
  fetchObjectRelationshipsRequest,
  fetchObjectRelationshipsSuccess,
  fetchObjectRelationshipsFailure,
  fetchObjectMappingSuccess,
} = slice.actions;

export const fetchObjectRelationships = createAppAsyncThunk<void, EAppId>(
  'objectRelationships/fetchObjectRelationships',
  async (appId, { dispatch }) => {
    try {
      dispatch(fetchObjectRelationshipsRequest());
      const result = await api.get({
        endpoint: `/apps/${appId}/object-relationships`,
      });
      dispatch(fetchObjectRelationshipsSuccess(result));
    } catch (e) {
      dispatch(fetchObjectRelationshipsFailure());
      console.error(e);
    }
  }
);

export const fetchObjectMapping = createAppAsyncThunk<void, EAppId>(
  'objectRelationships/fetchObjectMapping',
  async (appId, { dispatch }) => {
    try {
      dispatch(fetchObjectRelationshipsRequest());
      const result = await api.get({
        endpoint: `/apps/${appId}/mapping`,
      });
      dispatch(fetchObjectMappingSuccess(result));
    } catch (e) {
      dispatch(fetchObjectRelationshipsFailure());
      console.error(e);
    }
  }
);

type TSaveRelationshipsPayload = {
  appId: string;
  relationships: IObjectRelationship[];
};

export const saveRelationships = createAppAsyncThunk<
  void,
  TSaveRelationshipsPayload
>(
  'objectRelationships/saveRelationships',
  async ({ appId, relationships }, { dispatch }) => {
    try {
      dispatch(fetchObjectRelationshipsRequest());
      const payload = relationships.map((re: IObjectRelationship) =>
        _.omit(re, '_id')
      );
      const result = await api.patch({
        endpoint: `/apps/${appId}/object-relationships`,
        data: {
          relationships: payload,
        },
      });
      dispatch(fetchObjectRelationshipsSuccess(result));
    } catch (e) {
      dispatch(fetchObjectRelationshipsFailure());
      console.error(e);
    }
  }
);
